import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { DEVELOPMENT_ONLY_FEATURE_FLAGS, FeatureFlagSet } from '@/featureFlags';

export interface FeatureFlagContext {
  featureFlags: FeatureFlagSet;
  setFeatureFlags: Dispatch<SetStateAction<FeatureFlagSet>>;
  inContext: boolean;
}

const devFlagOverrides =
  import.meta.env.MODE === 'development' ? DEVELOPMENT_ONLY_FEATURE_FLAGS : undefined;

const FeatureFlagContextContext = createContext<FeatureFlagContext>({
  featureFlags: {},
  setFeatureFlags: () => {},
  inContext: false,
});

export interface FeatureFlagContextProps {
  children: ReactNode;
  testOverrides?: FeatureFlagSet;
}

/**
 * Note -- we don't actually make any requests to launchdarkly
 * unless we're in prod right now!!
 */
function FeatureFlagContextProvider({
  children,
  testOverrides,
}: FeatureFlagContextProps) {
  const ldFlags = useFlags();

  const shouldUseOverrides = Boolean(testOverrides) || Boolean(devFlagOverrides);

  const initialFlags = shouldUseOverrides
    ? { ...devFlagOverrides, ...testOverrides }
    : ldFlags;

  const [featureFlags, setFeatureFlags] = useState<FeatureFlagSet>(initialFlags);

  const value = { featureFlags, setFeatureFlags, inContext: true };

  // If we get a new set of flags from the launchdarkly api, update our flag state.
  // By checking if the list of flags exist, we avoid resetting flags to empty during cypress tests.
  useEffect(() => {
    if (!shouldUseOverrides && Object.keys(ldFlags).length > 0) {
      setFeatureFlags(ldFlags);
    }
  }, [shouldUseOverrides, ldFlags]);

  return (
    <FeatureFlagContextContext.Provider value={value}>
      {children}
    </FeatureFlagContextContext.Provider>
  );
}

// Note: You should never need to use this directly.
// This is used as a way to pass custom feature flags for our tests.
function useSetFeatureFlags(): Dispatch<SetStateAction<FeatureFlagSet>> {
  const context = useContext(FeatureFlagContextContext);

  if (!context.inContext) {
    throw new Error(
      'useSetFeatureFlags must be used within a FeatureFlagContextProvider',
    );
  }

  return context.setFeatureFlags;
}

function useFeatureFlags(): FeatureFlagSet {
  const context = useContext(FeatureFlagContextContext);

  if (!context.inContext) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagContextProvider');
  }

  return context.featureFlags;
}

function useFeatureFlag<TKey extends keyof FeatureFlagSet>(
  flag: TKey,
  defaultValue: Exclude<FeatureFlagSet[TKey], undefined>,
): Exclude<FeatureFlagSet[TKey], undefined> {
  const flags = useFeatureFlags();
  const result = flags[flag];
  if (result === undefined) return defaultValue;
  return result as Exclude<FeatureFlagSet[TKey], undefined>;
}

export {
  FeatureFlagContextProvider,
  useFeatureFlag,
  useFeatureFlags,
  useSetFeatureFlags,
};
